/**
 * Anlegen und Bearbeiten eines Artikels, mit Bestandshistorie.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  formatDate,
  formatPrice,
  formatQuantity,
  ITEM_KINDS,
  TAX_CODES,
  unitList,
} from '@erp/shared';

import { ApiError, api, withQuery } from '../api/client';
import type { Item, ItemComponent, ItemVariant, ListResponse, Partner, SetSummary, StockMovement } from '../api/types';
import { useAuth } from '../auth';
import { ComponentsCard, VariantAttributesEditor, VariantsCard } from '../components/ItemStructure';
import { PageHeader } from '../components/Layout';
import { SearchSelect } from '../components/SearchSelect';
import { useToast } from '../components/toast';
import {
  Alert,
  Checkbox,
  Dialog,
  EmptyState,
  Field,
  LoadingBlock,
  NumericInput,
  Select,
  TextArea,
  TextInput,
  CollapsibleTitle,
  useCollapsed,
} from '../components/ui';
import type { Category } from './CategoriesPage';

type ItemForm = {
  number: string;
  name: string;
  description: string;
  gtin: string;
  manufacturer: string;
  manufacturerNumber: string;
  categoryId: string;
  unit: string;
  taxCode: string;
  salesPrice: number;
  purchasePrice: number;
  isStockItem: boolean;
  minStock: number;
  reorderQuantity: number;
  supplierId: string;
  supplierItemNumber: string;
  leadTimeDays: number;
  revenueAccount: string;
  expenseAccount: string;
  weightGrams: number;
  customsTariffNumber: string;
  countryOfOrigin: string;
  grossWeightGrams: number;
  packagingUnit: string;
  /** Lagerplatz je Lager (Kennung des Lagers -> Platz); die Akte zeigt vorerst nur das Hauptlager. */
  storageBins: Record<string, string>;
  pickingStrategy: string;
  trackingMode: string;
  hasExpiry: boolean;
  notes: string;
  active: boolean;
  /** Art: standard | parent | set. */
  kind: string;
  /** "Variante von" (Kennung des Elternartikels). */
  parentItemId: string;
  variantAttributes: Record<string, string>;
};

const EMPTY: ItemForm = {
  number: '',
  name: '',
  description: '',
  gtin: '',
  manufacturer: '',
  manufacturerNumber: '',
  categoryId: '',
  unit: 'STK',
  taxCode: 'USt19',
  salesPrice: 0,
  purchasePrice: 0,
  isStockItem: true,
  minStock: 0,
  reorderQuantity: 0,
  supplierId: '',
  supplierItemNumber: '',
  leadTimeDays: 0,
  revenueAccount: '',
  expenseAccount: '',
  weightGrams: 0,
  customsTariffNumber: '',
  countryOfOrigin: '',
  grossWeightGrams: 0,
  packagingUnit: '',
  storageBins: {},
  pickingStrategy: 'fifo',
  trackingMode: 'none',
  hasExpiry: false,
  notes: '',
  active: true,
  kind: 'standard',
  parentItemId: '',
  variantAttributes: {},
};

/** Antwort der Artikelakte. */
type LoadedItem = {
  item: Item;
  movements: StockMovement[];
  storageBins?: { warehouseId: string; bin: string }[];
  parent: { id: string; number: string; name: string } | null;
  variants: ItemVariant[];
  components: ItemComponent[];
  setSummary: SetSummary | null;
  usedIn: { id: string; number: string; name: string; quantity: number }[];
};

const MOVEMENT_LABELS: Record<string, string> = {
  goods_receipt: 'Wareneingang',
  delivery: 'Lieferung',
  correction: 'Korrektur',
  inventory: 'Inventur',
  scrap: 'Ausbuchung',
  transfer_in: 'Umlagerung zu',
  transfer_out: 'Umlagerung von',
  opening_balance: 'Anfangsbestand',
  complaint_return: 'Rücklauf (Reklamation)',
  repair_consumption: 'Verbrauch (Reparatur)',
};

export function ItemDetailPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'neu' || id === undefined;
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [movementsCollapsed, toggleMovements] = useCollapsed('artikel-lagerbewegungen');
  const { can } = useAuth();
  const readOnly = !can('masterdata:write');

  const [form, setForm] = useState<ItemForm | null>(isNew ? EMPTY : null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<{ categories: Category[] }>('/api/categories'),
  });
  // Vorgaben der gewaehlten Warengruppe - ein Artikel ohne eigene Zolldaten erbt sie.
  const categoryDefaults = (categories?.categories ?? []).find((c) => c.id === form?.categoryId) as
    | { customsTariffNumber?: string | null; countryOfOrigin?: string | null }
    | undefined;

  // Lager fuer die Lagerplaetze je Lager.
  const { data: warehouses } = useQuery({
    queryKey: ['warehouses'],
    queryFn: () =>
      api.get<{ warehouses: { id: string; code: string; name: string; active: boolean; isDefault?: boolean }[] }>(
        '/api/stock/warehouses',
      ),
  });

  const { data: suppliers } = useQuery({
    queryKey: ['partners', 'supplier', 'all'],
    queryFn: () =>
      api.get<ListResponse<Partner>>(withQuery('/api/partners', { role: 'supplier', limit: 500 })),
  });

  useQuery({
    queryKey: ['item-next-number'],
    queryFn: async () => {
      const result = await api.get<{ number: string }>('/api/items/next-number');
      setForm((current) => (current && !current.number ? { ...current, number: result.number } : current));
      return result;
    },
    enabled: isNew,
  });

  const { data: loaded, isLoading } = useQuery({
    queryKey: ['item', id],
    queryFn: async () => {
      return api.get<LoadedItem>(`/api/items/${id}`);
    },
    enabled: !isNew,
  });

  // Das Formular entsteht aus den geladenen Daten - nicht in der Ladefunktion:
  // Kommt der Artikel aus dem Zwischenspeicher (innerhalb der staleTime), laeuft
  // die Ladefunktion nicht, und die Seite bliebe sonst bei "Wird geladen".
  // Ein Hintergrund-Refresh desselben Artikels ueberschreibt keine Eingaben,
  // zieht aber die errechneten Set-Preise nach.
  const loadedItemId = useRef<string | null>(null);
  useEffect(() => {
    if (isNew || !loaded) return;
    if (loadedItemId.current === loaded.item.id) {
      if (loaded.item.kind === 'set') {
        setForm((current) => (current ? { ...current, purchasePrice: loaded.item.purchasePrice } : current));
      }
      return;
    }
    loadedItemId.current = loaded.item.id;
    setForm(formFromLoaded(loaded));
    setErrors({});
  }, [loaded, isNew]);

  function formFromLoaded(result: LoadedItem): ItemForm {
    const item = result.item;
    return {
        number: item.number,
        name: item.name,
        description: item.description ?? '',
        gtin: item.gtin ?? '',
        manufacturer: item.manufacturer ?? '',
        manufacturerNumber: item.manufacturerNumber ?? '',
        categoryId: item.categoryId ?? '',
        unit: item.unit,
        taxCode: item.taxCode,
        salesPrice: item.salesPrice,
        purchasePrice: item.purchasePrice,
        isStockItem: item.isStockItem,
        minStock: item.minStock,
        reorderQuantity: item.reorderQuantity,
        supplierId: item.supplierId ?? '',
        supplierItemNumber: item.supplierItemNumber ?? '',
        leadTimeDays: item.leadTimeDays,
        revenueAccount: item.revenueAccount ?? '',
        expenseAccount: item.expenseAccount ?? '',
        weightGrams: item.weightGrams,
        customsTariffNumber: item.customsTariffNumber ?? '',
        countryOfOrigin: item.countryOfOrigin ?? '',
        grossWeightGrams: item.grossWeightGrams,
        packagingUnit: item.packagingUnit ?? '',
        storageBins: Object.fromEntries((result.storageBins ?? []).map((entry) => [entry.warehouseId, entry.bin])),
        pickingStrategy: item.pickingStrategy ?? 'fifo',
        trackingMode: item.trackingMode ?? 'none',
        hasExpiry: item.hasExpiry ?? false,
        notes: item.notes ?? '',
        active: item.active,
        kind: item.kind ?? 'standard',
        parentItemId: item.parentItemId ?? '',
        variantAttributes: item.variantAttributes ?? {},
    };
  }

  // Von einer Akte zu "Neuer Artikel" (z. B. "Neue Variante") wechselt nur die
  // URL, die Seite bleibt - das Formular muss von Hand zuruecksetzen, und die
  // Vorbelegungen (naechste Nummer, Elternartikel) neu laufen.
  const variantOf = searchParams.get('variantOf');
  // "Neu anlegen" aus der Liste der Set-Artikel bringt die Art gleich mit.
  const kindParam = searchParams.get('kind');
  useEffect(() => {
    if (!isNew) return;
    loadedItemId.current = null;
    setForm(kindParam === 'set' || kindParam === 'parent' ? { ...EMPTY, kind: kindParam, isStockItem: false } : EMPTY);
    setErrors({});
    queryClient.invalidateQueries({ queryKey: ['item-next-number'] });
    if (variantOf) queryClient.invalidateQueries({ queryKey: ['item', 'variant-template', variantOf] });
  }, [id, isNew, variantOf, kindParam, queryClient]);

  // Elternartikel fuer "Variante von".
  const { data: parents } = useQuery({
    queryKey: ['items', 'parents'],
    queryFn: () => api.get<ListResponse<Item>>(withQuery('/api/items', { kind: 'parent', active: 'all', limit: 500 })),
  });

  // Neue Variante aus der Akte des Elternartikels: Vorgaben uebernehmen.
  useQuery({
    queryKey: ['item', 'variant-template', variantOf],
    queryFn: async () => {
      const result = await api.get<{ item: Item }>(`/api/items/${variantOf}`);
      const parent = result.item;
      setForm((current) =>
        current
          ? {
              ...current,
              name: current.name || parent.name,
              description: current.description || (parent.description ?? ''),
              categoryId: current.categoryId || (parent.categoryId ?? ''),
              unit: parent.unit,
              taxCode: parent.taxCode,
              manufacturer: current.manufacturer || (parent.manufacturer ?? ''),
              supplierId: current.supplierId || (parent.supplierId ?? ''),
              customsTariffNumber: current.customsTariffNumber || (parent.customsTariffNumber ?? ''),
              countryOfOrigin: current.countryOfOrigin || (parent.countryOfOrigin ?? ''),
              parentItemId: parent.id,
              variantAttributes: Object.fromEntries(Object.keys(parent.variantAttributes ?? {}).map((name) => [name, ''])),
            }
          : current,
      );
      return result;
    },
    enabled: isNew && Boolean(variantOf),
  });

  const save = useMutation({
    mutationFn: (values: ItemForm) => {
      const payload = {
        ...values,
        description: values.description || null,
        gtin: values.gtin || null,
        manufacturer: values.manufacturer || null,
        manufacturerNumber: values.manufacturerNumber || null,
        categoryId: values.categoryId || null,
        supplierId: values.supplierId || null,
        supplierItemNumber: values.supplierItemNumber || null,
        revenueAccount: values.revenueAccount || null,
        customsTariffNumber: values.customsTariffNumber || null,
        countryOfOrigin: values.countryOfOrigin || null,
        packagingUnit: values.packagingUnit || null,
        storageBins: Object.entries(values.storageBins).map(([warehouseId, bin]) => ({ warehouseId, bin })),
        expenseAccount: values.expenseAccount || null,
        notes: values.notes || null,
        parentItemId: values.kind === 'parent' ? null : values.parentItemId || null,
      };

      return isNew
        ? api.post<{ item: Item }>('/api/items', payload)
        : api.patch<{ item: Item }>(`/api/items/${id}`, payload);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['item', id] });
      toast.success('Gespeichert.');
      if (isNew) navigate(`/artikel/${result.item.id}`, { replace: true });
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setErrors(error.fieldErrors);
        toast.error(error.message);
      } else {
        toast.error('Beim Speichern ist ein Fehler aufgetreten.');
      }
    },
  });

  const remove = useMutation({
    mutationFn: () =>
      api.delete<{ deleted: boolean; deactivated: boolean; message?: string }>(`/api/items/${id}`),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      setConfirmDelete(false);
      if (result.deleted) {
        toast.success('Der Artikel wurde gelöscht.');
        navigate('/artikel');
      } else {
        toast.info(result.message ?? 'Der Artikel wurde deaktiviert.');
        queryClient.invalidateQueries({ queryKey: ['item', id] });
      }
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : 'Löschen fehlgeschlagen.'),
  });

  if (!isNew && isLoading) return <LoadingBlock />;
  if (!form) return <LoadingBlock />;

  function update<K extends keyof ItemForm>(key: K, value: ItemForm[K]) {
    setForm((current) => (current ? { ...current, [key]: value } : current));
    setErrors((current) => {
      if (!current[key as string]) return current;
      const next = { ...current };
      delete next[key as string];
      return next;
    });
  }

  const margin =
    form.salesPrice > 0 && form.purchasePrice > 0
      ? ((form.salesPrice - form.purchasePrice) / form.salesPrice) * 100
      : null;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        setErrors({});
        save.mutate(form);
      }}
    >
      <PageHeader
        title={isNew ? 'Neuer Artikel' : `${form.number} · ${form.name}`}
        actions={
          <>
            <button type="button" className="btn" onClick={() => navigate('/artikel')}>
              Zurück
            </button>
            {!isNew && id ? (
              <button
                type="button"
                className="btn"
                onClick={() =>
                  navigate(
                    `/auswertungen/berichte?report=item-history&itemId=${id}&itemLabel=${encodeURIComponent(`${form.number} · ${form.name}`)}`,
                  )
                }
              >
                Historie
              </button>
            ) : null}
            {!isNew && !readOnly ? (
              <button type="button" className="btn btn-danger" onClick={() => setConfirmDelete(true)}>
                Löschen
              </button>
            ) : null}
            {!readOnly ? (
              <button type="submit" className="btn btn-primary" disabled={save.isPending}>
                {save.isPending ? 'Wird gespeichert …' : 'Speichern'}
              </button>
            ) : null}
          </>
        }
      />

      {!form.active ? <Alert kind="warning">Dieser Artikel ist deaktiviert.</Alert> : null}

      <div className="card">
        <div className="card-body">
          <fieldset>
            <legend>Artikel</legend>
            <div className="form-grid">
              <Field label="Artikelnummer" span={3} required error={errors.number}>
                <TextInput
                  value={form.number}
                  onChange={(value) => update('number', value)}
                  invalid={Boolean(errors.number)}
                  disabled={readOnly}
                />
              </Field>
              <Field label="Bezeichnung" span={9} required error={errors.name}>
                <TextInput
                  value={form.name}
                  onChange={(value) => update('name', value)}
                  invalid={Boolean(errors.name)}
                  disabled={readOnly}
                />
              </Field>
              <Field label="Beschreibung" span={12} error={errors.description}>
                <TextArea
                  value={form.description}
                  onChange={(value) => update('description', value)}
                  rows={2}
                  disabled={readOnly}
                />
              </Field>

              <Field
                label="Art"
                span={4}
                error={errors.kind}
                hint={
                  form.kind === 'parent'
                    ? 'Klammer für Varianten: kein Bestand, kein Preis, nicht im Beleg'
                    : form.kind === 'set'
                      ? 'Eigener Preis, kein Bestand - der Lieferschein bucht die Bestandteile'
                      : undefined
                }
              >
                <Select
                  value={form.kind}
                  onChange={(value) => {
                    update('kind', value);
                    if (value === 'parent' || value === 'set') update('isStockItem', false);
                  }}
                  options={ITEM_KINDS.map((entry) => ({ value: entry.value, label: entry.label }))}
                  disabled={readOnly}
                />
              </Field>
              {form.kind !== 'parent' ? (
                <Field label="Variante von" span={8} error={errors.parentItemId} hint="Elternartikel, unter dem dieser Artikel als Variante erscheint">
                  <SearchSelect
                    value={form.parentItemId}
                    onChange={(value) => {
                      update('parentItemId', value);
                      // Merkmalsnamen des Elternartikels vorgeben.
                      const parent = (parents?.items ?? []).find((entry) => entry.id === value);
                      if (parent) {
                        const names = Object.keys(parent.variantAttributes ?? {});
                        update('variantAttributes', {
                          ...Object.fromEntries(names.map((name) => [name, form.variantAttributes[name] ?? ''])),
                          ...form.variantAttributes,
                        });
                      }
                    }}
                    placeholder="Elternartikel tippen …"
                    emptyLabel="Keine Variante"
                    options={(parents?.items ?? [])
                      .filter((entry) => entry.id !== id)
                      .map((entry) => ({ value: entry.id, label: `${entry.number} · ${entry.name}` }))}
                    disabled={readOnly}
                  />
                </Field>
              ) : null}
              {form.kind === 'parent' || form.parentItemId ? (
                <Field
                  label={form.kind === 'parent' ? 'Merkmale der Varianten' : 'Merkmale dieser Variante'}
                  span={12}
                  hint={
                    form.kind === 'parent'
                      ? 'Nur die Namen, z. B. Farbe, Durchmesser - die Werte stehen an jeder Variante'
                      : 'Unterscheidet die Variante von ihren Geschwistern, z. B. Farbe rot'
                  }
                >
                  <VariantAttributesEditor
                    value={form.variantAttributes}
                    onChange={(value) => update('variantAttributes', value)}
                    namesOnly={form.kind === 'parent'}
                    disabled={readOnly}
                  />
                </Field>
              ) : null}
              {loaded?.parent ? (
                <Field label="Elternartikel" span={12}>
                  <Link to={`/artikel/${loaded.parent.id}`}>
                    {loaded.parent.number} · {loaded.parent.name}
                  </Link>
                </Field>
              ) : null}
              {loaded && loaded.usedIn.length > 0 ? (
                <Field label="Bestandteil von" span={12}>
                  <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
                    {loaded.usedIn.map((set) => (
                      <Link key={set.id} to={`/artikel/${set.id}`}>
                        {set.number} · {set.name} ({formatQuantity(set.quantity)} je Set)
                      </Link>
                    ))}
                  </div>
                </Field>
              ) : null}
              <Field label="EAN / GTIN" span={3} error={errors.gtin}>
                <TextInput value={form.gtin} onChange={(value) => update('gtin', value)} disabled={readOnly} />
              </Field>
              <Field label="Hersteller" span={4} error={errors.manufacturer}>
                <TextInput
                  value={form.manufacturer}
                  onChange={(value) => update('manufacturer', value)}
                  disabled={readOnly}
                />
              </Field>
              <Field label="Hersteller-Artikelnummer" span={5} error={errors.manufacturerNumber}>
                <TextInput
                  value={form.manufacturerNumber}
                  onChange={(value) => update('manufacturerNumber', value)}
                  disabled={readOnly}
                />
              </Field>
              <Field
                label="Warengruppe"
                span={6}
                error={errors.categoryId}
                hint="Steuert Auswertung und, falls hinterlegt, das Erlöskonto"
              >
                <SearchSelect
                  value={form.categoryId}
                  onChange={(value) => update('categoryId', value)}
                  placeholder="Warengruppe tippen …"
                  emptyLabel="Keine Warengruppe"
                  options={(categories?.categories ?? [])
                    .filter((category) => category.active || category.id === form.categoryId)
                    .map((category) => ({
                      value: category.id,
                      label: category.number
                        ? `${category.number} · ${category.name}`
                        : category.name,
                    }))}
                  disabled={readOnly}
                />
              </Field>
            </div>
          </fieldset>

          <fieldset>
            <legend>Preise</legend>
            <div className="form-grid">
              <Field label="Verkaufspreis netto" span={3} error={errors.salesPrice}>
                <NumericInput
                  kind="price"
                  value={form.salesPrice}
                  onChange={(value) => update('salesPrice', value)}
                  disabled={readOnly}
                />
              </Field>
              <Field
                label="Einkaufspreis netto"
                span={3}
                error={errors.purchasePrice}
                hint={form.kind === 'set' ? 'Summe der Einkaufspreise der Bestandteile, rechnet sich selbst' : undefined}
              >
                <NumericInput
                  kind="price"
                  value={form.purchasePrice}
                  onChange={(value) => update('purchasePrice', value)}
                  disabled={readOnly || form.kind === 'set'}
                />
              </Field>
              <Field
                label="Rohertrag"
                span={2}
                hint={
                  margin === null
                    ? undefined
                    : loaded && loaded.item.averageCost > 0 && form.salesPrice > 0
                      ? `zum Einkaufspreis · zum Einstand ${formatPrice(form.salesPrice - loaded.item.averageCost)} € = ${(((form.salesPrice - loaded.item.averageCost) / form.salesPrice) * 100).toFixed(1)} %`
                      : 'auf den Verkaufspreis, zum Einkaufspreis'
                }
              >
                <input autoComplete="off"
                  className="input numeric"
                  value={margin === null ? '—' : `${formatPrice(form.salesPrice - form.purchasePrice)} € · ${margin.toFixed(1)} %`}
                  readOnly
                  disabled
                />
              </Field>
              <Field label="Einheit" span={2} error={errors.unit}>
                <Select
                  value={form.unit}
                  onChange={(value) => update('unit', value)}
                  options={unitList().map((unit) => ({ value: unit.code, label: unit.label }))}
                  disabled={readOnly}
                />
              </Field>
              <Field label="Steuersatz" span={2} error={errors.taxCode}>
                <Select
                  value={form.taxCode}
                  onChange={(value) => update('taxCode', value)}
                  options={Object.values(TAX_CODES).map((code) => ({
                    value: code.code,
                    label: code.label,
                  }))}
                  disabled={readOnly}
                />
              </Field>
            </div>
          </fieldset>

          <fieldset>
            <legend>Lager und Beschaffung</legend>
            <div className="form-grid">
              <Field
                label="Lagerführung"
                span={3}
                hint={form.kind === 'parent' ? 'Bestand liegt an den Varianten' : form.kind === 'set' ? 'Bestand liegt an den Bestandteilen' : undefined}
              >
                <div style={{ paddingTop: 5 }}>
                  <Checkbox
                    id="isStockItem"
                    checked={form.isStockItem}
                    onChange={(value) => update('isStockItem', value)}
                    label="Bestand führen"
                    disabled={readOnly || form.kind === 'parent' || form.kind === 'set'}
                  />
                </div>
              </Field>

              {form.isStockItem ? (
                <>
                  <Field
                    label="Aktueller Bestand"
                    span={3}
                    hint="Nur über Lagerbewegungen änderbar"
                  >
                    <input autoComplete="off"
                      className="input numeric"
                      value={loaded ? formatQuantity(loaded.item.stockQuantity) : '0'}
                      readOnly
                      disabled
                    />
                  </Field>
                  <Field label="Meldebestand" span={3} error={errors.minStock}>
                    <NumericInput
                      kind="quantity"
                      value={form.minStock}
                      onChange={(value) => update('minStock', value)}
                      disabled={readOnly}
                    />
                  </Field>
                  <Field label="Bestellmenge" span={3} error={errors.reorderQuantity}>
                    <NumericInput
                      kind="quantity"
                      value={form.reorderQuantity}
                      onChange={(value) => update('reorderQuantity', value)}
                      disabled={readOnly}
                    />
                  </Field>
                </>
              ) : null}

              <Field label="Hauptlieferant" span={5} error={errors.supplierId}>
                <SearchSelect
                  value={form.supplierId}
                  onChange={(value) => update('supplierId', value)}
                  placeholder="Lieferant tippen …"
                  emptyLabel="Kein fester Lieferant"
                  options={(suppliers?.items ?? []).map((supplier) => ({
                    value: supplier.id,
                    label: `${supplier.number} · ${supplier.name}`,
                  }))}
                  disabled={readOnly}
                />
              </Field>
              <Field label="Artikelnummer beim Lieferanten" span={4} error={errors.supplierItemNumber}>
                <TextInput
                  value={form.supplierItemNumber}
                  onChange={(value) => update('supplierItemNumber', value)}
                  disabled={readOnly}
                />
              </Field>
              <Field label="Lieferzeit in Tagen" span={3} error={errors.leadTimeDays}>
                <input autoComplete="off"
                  className="input numeric"
                  type="number"
                  min={0}
                  value={form.leadTimeDays}
                  disabled={readOnly}
                  onChange={(event) => update('leadTimeDays', Number(event.target.value) || 0)}
                />
              </Field>
            </div>
          </fieldset>

          <fieldset>
            <legend>Lagerplatz und Entnahme</legend>
            <div className="form-grid">
              {/* Vorerst nur das Hauptlager; weitere Lager kommen, wenn sie gebraucht werden. */}
              {(warehouses?.warehouses ?? [])
                .filter((warehouse) => warehouse.active && warehouse.isDefault)
                .map((warehouse) => (
                  <Field
                    key={warehouse.id}
                    label={`Lagerplatz · ${warehouse.name}`}
                    span={4}
                    hint="Regal · Reihe · Fach, wird am Wareneingang vorgeschlagen"
                  >
                    <TextInput
                      value={form.storageBins[warehouse.id] ?? ''}
                      onChange={(value) => update('storageBins', { ...form.storageBins, [warehouse.id]: value })}
                      placeholder="z. B. M-A-221"
                      disabled={readOnly}
                    />
                  </Field>
                ))}
              <Field
                label="Chargenführung"
                span={4}
                error={errors.trackingMode}
                hint="Was der Wareneingang je Position verlangt"
              >
                <Select
                  value={form.trackingMode}
                  onChange={(value) => update('trackingMode', value)}
                  options={[
                    { value: 'none', label: 'Keine (Zugänge ohne Nummer)' },
                    { value: 'batch', label: 'Chargennummer je Zugang' },
                    { value: 'serial', label: 'Seriennummer je Stück' },
                  ]}
                  disabled={readOnly}
                />
              </Field>
              <Field label="Haltbarkeit" span={4} error={errors.hasExpiry}>
                <Checkbox
                  id="hasExpiry"
                  checked={form.hasExpiry}
                  onChange={(value) => update('hasExpiry', value)}
                  label="Mindesthaltbarkeitsdatum je Charge erfassen"
                  disabled={readOnly}
                />
              </Field>
              <Field
                label="Entnahme"
                span={4}
                error={errors.pickingStrategy}
                hint="Welcher Zugang beim Lieferschein zuerst vorgeschlagen wird"
              >
                <Select
                  value={form.pickingStrategy}
                  onChange={(value) => update('pickingStrategy', value)}
                  options={[
                    { value: 'fifo', label: 'First in – First out (ältester Zugang zuerst)' },
                    ...(form.hasExpiry ? [{ value: 'fefo', label: 'Kürzeste Haltbarkeit zuerst' }] : []),
                    { value: 'manual', label: 'Keine Vorgabe (von Hand wählen)' },
                  ]}
                  disabled={readOnly}
                />
              </Field>
            </div>
          </fieldset>

          <fieldset>
            <legend>Zoll und Logistik</legend>
            <div className="form-grid">
              <Field
                label="Warennummer"
                span={3}
                error={errors.customsTariffNumber}
                hint={
                  !form.customsTariffNumber && categoryDefaults?.customsTariffNumber
                    ? `Vorgabe der Warengruppe: ${categoryDefaults.customsTariffNumber}`
                    : '8 Ziffern (Kombinierte Nomenklatur), für Ausfuhr und Intrastat'
                }
              >
                <TextInput
                  value={form.customsTariffNumber}
                  onChange={(value) => update('customsTariffNumber', value.replace(/\D/g, ''))}
                  placeholder={categoryDefaults?.customsTariffNumber ?? ''}
                  maxLength={8}
                  disabled={readOnly}
                />
              </Field>
              <Field
                label="Ursprungsland"
                span={2}
                error={errors.countryOfOrigin}
                hint={
                  !form.countryOfOrigin && categoryDefaults?.countryOfOrigin
                    ? `Vorgabe: ${categoryDefaults.countryOfOrigin}`
                    : 'Ländercode'
                }
              >
                <TextInput
                  value={form.countryOfOrigin}
                  onChange={(value) => update('countryOfOrigin', value.toUpperCase())}
                  placeholder={categoryDefaults?.countryOfOrigin ?? 'DE'}
                  maxLength={2}
                  disabled={readOnly}
                />
              </Field>
              <Field label="Nettogewicht (g)" span={2} error={errors.weightGrams} hint="Eigenmasse je Einheit">
                <input
                  autoComplete="off"
                  className="input numeric"
                  type="number"
                  min={0}
                  value={form.weightGrams}
                  disabled={readOnly}
                  onChange={(event) => update('weightGrams', Number(event.target.value) || 0)}
                />
              </Field>
              <Field label="Bruttogewicht (g)" span={2} error={errors.grossWeightGrams} hint="mit Verpackung">
                <input
                  autoComplete="off"
                  className="input numeric"
                  type="number"
                  min={0}
                  value={form.grossWeightGrams}
                  disabled={readOnly}
                  onChange={(event) => update('grossWeightGrams', Number(event.target.value) || 0)}
                />
              </Field>
              <Field label="Verpackungseinheit" span={3} error={errors.packagingUnit} hint="z. B. Rolle 50 m">
                <TextInput
                  value={form.packagingUnit}
                  onChange={(value) => update('packagingUnit', value)}
                  disabled={readOnly}
                />
              </Field>
            </div>
          </fieldset>

          <fieldset style={{ marginBottom: 0 }}>
            <legend>Notizen</legend>
            <TextArea
              value={form.notes}
              onChange={(value) => update('notes', value)}
              rows={2}
              disabled={readOnly}
            />
          </fieldset>
        </div>
      </div>

      {!isNew && loaded && loaded.item.kind === 'parent' ? (
        <VariantsCard parentId={loaded.item.id} variants={loaded.variants} canWrite={!readOnly} />
      ) : null}

      {!isNew && loaded && loaded.item.kind === 'set' ? (
        <ComponentsCard
          setId={loaded.item.id}
          components={loaded.components}
          summary={loaded.setSummary}
          salesPrice={form.salesPrice}
          canWrite={!readOnly}
        />
      ) : null}
      {isNew && form.kind === 'set' ? (
        <Alert kind="info">Die Stückliste lässt sich nach dem ersten Speichern anlegen.</Alert>
      ) : null}

      {!isNew && loaded && loaded.item.kind !== 'parent' && loaded.item.kind !== 'set' ? (
        <div className="card">
          <div className="card-body" style={{ paddingBottom: movementsCollapsed ? undefined : 0 }}>
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <CollapsibleTitle
                title="Lagerbewegungen"
                collapsed={movementsCollapsed}
                onToggle={toggleMovements}
                subtitle={`${loaded.movements.length} Bewegungen`}
              />
              <span className="faint">
                Einstandspreis gleitend: {formatPrice(loaded.item.averageCost)} €
              </span>
            </div>
          </div>
          {movementsCollapsed ? null : loaded.movements.length === 0 ? (
            <EmptyState
              title="Noch keine Bewegungen"
              hint="Bewegungen entstehen beim Wareneingang und beim Lieferschein."
            />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th className="shrink">Datum</th>
                    <th>Vorgang</th>
                    <th className="shrink">Beleg</th>
                    <th className="numeric">Menge</th>
                    <th className="numeric">Bestand danach</th>
                    <th className="numeric">Einstand</th>
                  </tr>
                </thead>
                <tbody>
                  {loaded.movements.map((movement) => (
                    <tr key={movement.id}>
                      <td>{formatDate(movement.movementDate)}</td>
                      <td>{MOVEMENT_LABELS[movement.movementType] ?? movement.movementType}</td>
                      <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                        {movement.documentId ? (
                          <Link to={`/belege/${movement.documentId}`} title="Beleg öffnen">
                            {movement.documentNumber ?? 'Beleg'}
                          </Link>
                        ) : (
                          movement.documentNumber ?? ''
                        )}
                      </td>
                      <td
                        className="numeric"
                        style={{ color: movement.quantity < 0 ? 'var(--danger)' : 'var(--success)' }}
                      >
                        {movement.quantity > 0 ? '+' : ''}
                        {formatQuantity(movement.quantity)}
                      </td>
                      <td className="numeric">{formatQuantity(movement.balanceAfter)}</td>
                      <td className="numeric muted">{formatPrice(movement.unitCost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {confirmDelete ? (
        <Dialog
          title="Artikel löschen?"
          onClose={() => setConfirmDelete(false)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setConfirmDelete(false)}>
                Abbrechen
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={remove.isPending}
                onClick={() => remove.mutate()}
              >
                Löschen
              </button>
            </>
          }
        >
          <p style={{ margin: 0 }}>
            <strong>{form.name}</strong> wird gelöscht. Steht der Artikel bereits auf Belegen, wird
            er stattdessen deaktiviert.
          </p>
        </Dialog>
      ) : null}
    </form>
  );
}
